import { type StopType } from 'core/domain/Stop'
import { type TruckSubType, type TruckType } from 'core/domain/Truck'
import { createDate, formatDate } from 'core/utils/date'

import { type BidStatus } from '../domain/BidStatus'
import { type EligibilityReason } from '../domain/EligibilityReason'
import {
  DEFAULT_MODES,
  DEFAULT_RADIUS_VALUE,
  DEFAULT_TRUCK_TYPES,
} from '../domain/filter/LoadboardFilter'
import { type IntermodalType } from '../domain/IntermodalType'
import { type Load } from '../domain/Load'
import { type LoadDimensions } from '../domain/LoadDimensions'
import { type LoadIntermodal } from '../domain/LoadIntermodal'
import { type LoadMode } from '../domain/LoadMode'
import { type LoadRequirements } from '../domain/LoadRequirements'
import { type LoadStop } from '../domain/LoadStop'
import { type Offer } from '../domain/Offer'
import { type OfferBid } from '../domain/OfferBid'
import { type OfferContract } from '../domain/OfferContract'
import { type OfferEligibility } from '../domain/OfferEligibility'
import { type PricingMethod } from '../domain/PricingMethod'
import { type ShipperMarket } from '../domain/ShipperMarket'
import { type TarpType } from '../domain/TarpType'

type RawOfferContract = {
  source: {
    type: string
    uuid: string
    is_dedicated_fleet?: boolean
  }
  carrier_contract?: {
    uuid: string
    owner_id: number
    representative_id: number
  }
}

type RawLoadIntermodal = {
  container: {
    id: number
    load_type: IntermodalType
    port_name: string
    equipment_type: string
    equipment_height: string
    equipment_length: string
    vessel_cutoff_date: string | null
    last_free_day_at_port: string | null
    vessel_eta_date: string | null
  }
}

type RawLoadStop = {
  stop_index: number
  type: StopType
  city: string
  state: string
  country: string
  zipcode: string
  timezone: string
  window_start: string
  window_end: string
  distance_from_previous_stop: number
  facility_id: string
  latitude: number
  longitude: number
  requirements: Array<string>
  tags: Array<string>
  confirmed?: boolean | null
}

type RawLoadDimensions = {
  weight: number | null
  length: number | null
  height: number | null
  width: number | null
}

type RawLoadRequirements = {
  tsa: boolean | null
  beer: boolean | null
  hvhr: boolean | null
  twic: boolean | null
  tarp: {
    size: number
    type: TarpType
  } | null
  ctpat: boolean | null
  teams: boolean | null
  frozen: boolean | null
  hazmat: boolean | null
  dunnage: boolean | null
  produce: boolean | null
  chemicals: boolean | null
  food_grade: boolean | null
  vented_vans: boolean | null
  drop_trailer: boolean | null
  pharmaceuticals: boolean | null
  dont_call_driver: boolean | null
}

export type RawLoad = {
  uuid: string
  created_at: string
  ref_number: string
  direct_from_shipper: boolean
  load_candidate: boolean
  shipper_id: string
  commodity: string | null
  temperature: number | null
  weight: number
  mileage: string
  customer_requirements: Array<string>
  equipment_type: TruckType
  intermodal: RawLoadIntermodal | null
  stops: Array<RawLoadStop>
  team_driver: boolean | null
  dimensions: RawLoadDimensions
  requirements: RawLoadRequirements | null
  equipment_subtypes: TruckSubType[] | null
  shipper_market: ShipperMarket | null
  tags: Array<string> | null
  mode: LoadMode
  must_arrive_by_date: string | null
}

type RawOfferBid = {
  uuid: string
  price: string
  status: BidStatus
  created_at: string
  expires_at: string
  expired_at: string | null
}

type RawOfferEligibility = {
  eligible: boolean
  reasons: Array<EligibilityReason> | null
}

export type RawOffer = {
  uuid: string
  created_at: string
  expires_at: string | null
  price: number
  contract: RawOfferContract | null
  load: RawLoad
  last_bid: RawOfferBid | null
  biddable: boolean
  eligibility: RawOfferEligibility
  pricing_method: PricingMethod
  offered_by_name: string | null
  daimler_vip?: boolean
}

export function mapStop(stop: RawLoadStop): LoadStop {
  return {
    stopIndex: stop.stop_index,
    type: stop.type,
    city: stop.city,
    state: stop.state,
    country: stop.country,
    zipcode: stop.zipcode,
    timezone: stop.timezone,
    windowStart: new Date(stop.window_start),
    windowEnd: new Date(stop.window_end),
    distanceFromPreviousStop: stop.distance_from_previous_stop,
    facilityId: stop.facility_id,
    latitude: stop.latitude,
    longitude: stop.longitude,
    requirements: stop.requirements,
    tags: stop.tags,
    confirmed: Boolean(stop.confirmed),
  }
}

export function mapRequirements(
  requirements: RawLoadRequirements | null
): LoadRequirements | undefined {
  if (requirements == null) return
  return {
    tsa: Boolean(requirements.tsa),
    beer: Boolean(requirements.beer),
    hvhr: Boolean(requirements.hvhr),
    twic: Boolean(requirements.twic),
    tarp: requirements.tarp ?? undefined,
    ctpat: Boolean(requirements.ctpat),
    teams: Boolean(requirements.teams),
    frozen: Boolean(requirements.frozen),
    hazmat: Boolean(requirements.hazmat),
    dunnage: Boolean(requirements.dunnage),
    produce: Boolean(requirements.produce),
    chemicals: Boolean(requirements.chemicals),
    foodGrade: Boolean(requirements.food_grade),
    ventedVans: Boolean(requirements.vented_vans),
    dropTrailer: Boolean(requirements.drop_trailer),
    pharmaceuticals: Boolean(requirements.pharmaceuticals),
    dontCallDriver: Boolean(requirements.dont_call_driver),
  }
}

export function mapContract(contract: RawOfferContract | null): OfferContract | undefined {
  if (contract == null) return
  const carrier_contract = contract?.carrier_contract

  let carrierContract

  if (carrier_contract) {
    carrierContract = {
      uuid: carrier_contract.uuid,
      ownerId: carrier_contract.owner_id,
      representativeId: carrier_contract.representative_id,
    }
  }

  return {
    carrierContract,
    source: {
      type: contract.source.type,
      uuid: contract.source.uuid,
      isDedicatedFleet: Boolean(contract.source?.is_dedicated_fleet),
    },
  }
}

export function mapLastBid(lastBid: RawOfferBid | null): OfferBid | undefined {
  if (lastBid == null) return
  return {
    uuid: lastBid.uuid,
    price: lastBid.price,
    status: lastBid.status,
    createdAt: new Date(lastBid.created_at),
    expiresAt: new Date(lastBid.expires_at),
    expiredAt: createDate(lastBid.expired_at),
  }
}

export function mapIntermodal(intermodal: RawLoadIntermodal | null): LoadIntermodal | undefined {
  if (intermodal == null) return
  return {
    container: {
      id: intermodal.container.id,
      loadType: intermodal.container.load_type,
      portName: intermodal.container.port_name,
      equipmentType: intermodal.container.equipment_type,
      equipmentHeight: intermodal.container.equipment_height,
      equipmentLength: intermodal.container.equipment_length,
      vesselCutoffDate: createDate(intermodal.container.vessel_cutoff_date),
      lastFreeDayAtPort: createDate(intermodal.container.last_free_day_at_port),
      vesselEtaDate: createDate(intermodal.container.vessel_eta_date),
    },
  }
}

export function mapDimensions(dimensions: RawLoadDimensions): LoadDimensions {
  return {
    length: dimensions.length ?? undefined,
    height: dimensions.height ?? undefined,
    width: dimensions.width ?? undefined,
  }
}

export function filterMapper(queryParams: Record<string, string>) {
  const pickupCoordinates = queryParams['pickupCoordinates']
  const deliveryCoordinates = queryParams['deliveryCoordinates']
  const pickupDateRangeStart =
    queryParams['pickupDateRangeStart'] ?? formatDate(new Date(), 'yyyy-MM-dd')
  const pickupDateRangeEnd = queryParams['pickupDateRangeEnd']
  const radius = queryParams['radius'] ?? DEFAULT_RADIUS_VALUE
  const filterByPreferredLanes = queryParams['filterByPreferredLanes']
  const mode = queryParams['modes[]'] ?? DEFAULT_MODES.join(',')
  const equipments = queryParams['equipments[]'] ?? DEFAULT_TRUCK_TYPES.join(',')
  const types = queryParams['type[]']
  const features = queryParams['features[]']

  // When using the preferred lanes filter, we must omit the lane filter
  const hasOrigin = pickupCoordinates && !filterByPreferredLanes
  const hasDelivery = deliveryCoordinates && !filterByPreferredLanes

  return {
    pickup_start_date: pickupDateRangeStart,
    mode: mode,
    equipment_type: equipments,
    ...(hasOrigin && { origin: `${pickupCoordinates},${radius}` }),
    ...(hasDelivery && { destination: `${deliveryCoordinates},${radius}` }),
    ...(pickupDateRangeEnd && { pickup_end_date: pickupDateRangeEnd }),
    ...(filterByPreferredLanes && { preferred_lane: filterByPreferredLanes }),
    ...(types && { market_tags: types }),
    ...(features && { features: features }),
  }
}

export function mapApiToOffer(rawOffer: RawOffer): Offer {
  if (rawOffer == null) {
    throw new TypeError(
      `Expected rawOffer, but received "${rawOffer === null ? 'null' : 'undefined'}"`
    )
  }

  const contract: OfferContract | undefined = mapContract(rawOffer.contract)

  const intermodal: LoadIntermodal | undefined = mapIntermodal(rawOffer.load.intermodal)

  const stops: Array<LoadStop> = rawOffer.load.stops.map(stop => mapStop(stop))

  const dimensions: LoadDimensions = mapDimensions(rawOffer.load.dimensions)

  const requirements: LoadRequirements | undefined = mapRequirements(rawOffer.load.requirements)

  const load: Load = {
    uuid: rawOffer.load.uuid,
    createdAt: new Date(rawOffer.load.created_at),
    refNumber: rawOffer.load.ref_number,
    directFromShipper: rawOffer.load.direct_from_shipper,
    shipperId: rawOffer.load.shipper_id,
    commodity: rawOffer.load.commodity ?? undefined,
    temperature: rawOffer.load.temperature ?? undefined,
    weight: rawOffer.load.weight,
    mileage: rawOffer.load.mileage,
    customerRequirements: rawOffer.load.customer_requirements,
    equipmentType: rawOffer.load.equipment_type,
    intermodal,
    stops,
    teamDriver: Boolean(rawOffer.load.team_driver),
    dimensions,
    requirements,
    equipmentSubtypes: rawOffer.load.equipment_subtypes ?? undefined,
    shipperMarket: rawOffer.load.shipper_market ?? undefined,
    tags: rawOffer.load.tags ?? undefined,
    mode: rawOffer.load.mode,
  }

  const lastBid: OfferBid | undefined = mapLastBid(rawOffer.last_bid)

  const eligibility: OfferEligibility = {
    eligible: rawOffer.eligibility.eligible,
    reasons: rawOffer.eligibility.reasons ?? undefined,
  }

  const offer: Offer = {
    uuid: rawOffer.uuid,
    createdAt: new Date(rawOffer.created_at),
    expiresAt: createDate(rawOffer.expires_at),
    price: rawOffer.price,
    contract,
    load,
    lastBid,
    biddable: rawOffer.biddable,
    eligibility,
    pricingMethod: rawOffer.pricing_method,
    offeredByName: rawOffer.offered_by_name ?? undefined,
    daimlerVip: rawOffer.daimler_vip,
  }

  return offer
}
